<template>
    <div>
        <div class="modal fade" id="courseEventsDatesModal" tabindex="-1" role="dialog"
             aria-labelledby="courseEventsDatesModalLabel" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered" role="document">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title" id="courseEventsDatesModalLabel">
                            {{ trans('forms.training.schedule_for') }} {{ courseEvent.name }}</h5>
                        <button type="button" class="close" @click="closeModal" aria-label="Close">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                    <div class="modal-body">
                        <div class="row mb-2">
                            <div class="col-sm-6">
                                <label v-if="courseEvent.scheduling === 'period'"
                                       for="start_at">{{ trans('forms.training.event_start_at') }}</label>
                                <label v-else for="start_at">{{ trans('forms.training.event_on') }}</label>
                                <input required :min="start_min_date" :max="start_max_date" id="start_at"
                                       class="form-control" @change="setMinDate"
                                       :class="{'is-invalid': invalidStartDate}" v-model="start_at" type="date"/>
                            </div>
                            <div v-if="courseEvent.scheduling === 'period'" class="col-sm-6">
                                <label for="end_at">{{ trans('forms.training.event_end_at') }}</label>
                                <input required :min="end_min_date" :max="end_max_date" id="end_at" class="form-control"
                                       @change="setMaxDate" :class="{'is-invalid': invalidEndDate}" v-model="end_at"
                                       type="date"/>
                            </div>
                        </div>
                        <div v-if="courseEvent.scheduling === 'period'" class="row">
                            <div class="col-sm-12">
                                <label>{{ trans('forms.training.completion_missed_email') }}</label>
                                <select v-model="completion_missed_email" class="form-control">
                                    <option :value="null">{{ trans('forms.training.do_not_send') }}</option>
                                    <option v-for="template in completionMissedEmails"
                                            :key="'comp_mis-' + template.slug"
                                            :value="template.slug">
                                        {{
                                            trans('forms').email_templates.templates.defaults[template.slug] || template.name
                                        }}
                                    </option>
                                </select>
                            </div>
                        </div>
                        <div class="row mb-2">
                            <div class="col-sm-12">
                                <label>{{ trans('forms.training.event_reminder_email') }}</label>
                                <select v-model="event_reminder_email" class="form-control">
                                    <option :value="null">{{ trans('forms.training.do_not_send') }}</option>
                                    <option v-for="template in eventReminderEmails"
                                            :key="'comp_mis-' + template.slug"
                                            :value="template.slug">
                                        {{
                                            trans('forms').email_templates.templates.defaults[template.slug] || template.name
                                        }}
                                    </option>
                                </select>
                            </div>
                        </div>
                        <transition name="fade">
                            <div v-if="event_reminder_email">
                                <div class="row mb-2">
                                    <div class="col-sm-6">
                                        <label for="start_at_time">{{ trans('forms.training.start_at_time') }}</label>
                                        <input id="start_at_time" class="form-control" v-model="start_at_time"
                                               type="time"/>
                                    </div>
                                </div>
                                <div class="row mb-2">
                                    <div class="col-sm-12">
                                        <label
                                            for="additional_info">{{ trans('forms.training.additional_info') }}</label>
                                        <textarea id="additional_info"
                                                  :placeholder="trans('forms.training.additional_info_placeholder')"
                                                  class="form-control"
                                                  v-model="additional_info"/>
                                    </div>
                                </div>
                                <div class="row my-2">
                                    <div class="col-sm-12">
                                        <label>{{ trans('forms.training.send_reminder') }}</label>
                                        <div v-for="(reminder, index) in reminders"
                                             :key="'reminder-' + index"
                                             class="form-group"
                                             :class="{
                                                 'has-error has-feedback': reminder.error,
                                             }"
                                        >
                                            <div class="flex mb-2">
                                                <input type="number"
                                                       min="1"
                                                       step="1"
                                                       :readonly="reminder.processed_at"
                                                       v-model="reminder.reminder_units"
                                                       pattern="[0-9]"
                                                       @keydown="checkNumber($event)"
                                                       class="form-control col-sm-2">
                                                <select :disabled="reminder.processed_at"
                                                        v-model="reminder.reminder_unit_type"
                                                        class="ml-2 form-control col-sm-3">
                                                    <option value="minute">
                                                        {{ trans('forms.training.reminder_options.minutes') }}
                                                    </option>
                                                    <option value="hour">
                                                        {{ trans('forms.training.reminder_options.hours') }}
                                                    </option>
                                                    <option value="day">
                                                        {{ trans('forms.training.reminder_options.days') }}
                                                    </option>
                                                    <option value="week">
                                                        {{ trans('forms.training.reminder_options.weeks') }}
                                                    </option>
                                                </select>
                                                <span class="ml-2 mr-4">{{ trans('forms.training.before') }}</span>
                                                <div class="action-menu mb-2">
                                                    <div class="action-button-menu" @click="addReminder()">
                                                        <img src="/images/svg-icons/marlms-plus-grey.svg" loading="lazy"
                                                             alt="" class="svg-with-hover">
                                                        <span
                                                            class="action-tooltip">{{ trans('forms.training.add_reminder') }}</span>
                                                    </div>
                                                    <div v-if="reminders.length > 1 && !reminder.processed_at"
                                                         class="action-button-menu" @click="removeReminder(index)">
                                                        <img src="/images/svg-icons/marlms-delete-action.svg"
                                                             loading="lazy" alt="" class="svg-with-hover">
                                                        <span
                                                            class="action-tooltip">{{ trans('forms.training.delete_reminder') }}</span>
                                                    </div>

                                                </div>
                                                <div v-if="reminder.processed_at" class="small">
                                                    {{ trans('forms.training.sent_at') }}
                                                    {{ moment(reminder.processed_at).format('DD.MM.YYYY HH:MM') }}
                                                </div>
                                            </div>
                                            <span v-if="reminder.error" class="help-block text-danger">{{
                                                    reminder.error
                                                }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </transition>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" @click="closeModal">{{
                                trans('forms.cancel')
                            }}
                        </button>
                        <button type="button" class="btn btn-primary" @click="save">{{ trans('forms.save') }}</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import moment from "moment";

export default {
    name: "CourseEventsDatesModal",
    props: {
        courseEvent: Object,
        training: Object,
        completionMissedEmails: Object,
        eventReminderEmails: Object,
    },
    mounted() {
        this.start_min_date = this.training.start_at;
        this.start_max_date = this.training.end_at;
        this.end_min_date = this.training.start_at;
        this.end_max_date = this.training.end_at;
        if (this.courseEvent.start_at) this.start_at = this.courseEvent.start_at;
        if (this.courseEvent.end_at) this.end_at = this.courseEvent.end_at;
        this.completion_missed_email = this.courseEvent.completion_missed_email || null;
        this.event_reminder_email = this.courseEvent.event_reminder_email || null;

        this.start_at_time = this.courseEvent.start_at_time || null;
        this.additional_info = this.courseEvent.additional_info || null;

        this.reminders = this.courseEvent.reminders || [this.newReminder()];

        $('#courseEventsDatesModal').modal({backdrop: 'static', keyboard: false}).modal('show')
    },
    data: function () {
        return {
            start_at: "",
            end_at: "",
            start_min_date: "",
            start_max_date: "",
            end_min_date: "",
            end_max_date: "",
            invalidStartDate: false,
            invalidEndDate: false,
            completion_missed_email: null,
            event_reminder_email: null,
            start_at_time: null,
            additional_info: null,
            reminders: [],
        }
    },
    methods: {
        moment,
        closeModal: function () {
            this.$emit('update-course-event', {save: false})
            $('#courseEventsDatesModal').modal('hide');
        },
        save: function () {
            if (this.validateDates()) {
                this.$emit('update-course-event', {
                    save: true,
                    start_at: this.start_at,
                    end_at: this.end_at,
                    completion_missed_email: this.completion_missed_email,
                    event_reminder_email: this.event_reminder_email,
                    start_at_time: this.start_at_time,
                    additional_info: this.additional_info,
                    reminders: this.reminders.map((reminder) => {
                        delete reminder.error;
                        return reminder;
                    }),
                });
                $('#courseEventsDatesModal').modal('hide');
            } else {
                this.$forceUpdate();
            }
        },
        validateDates() {
            this.invalidStartDate = this.start_at.length === 0;
            this.invalidEndDate = this.courseEvent.scheduling === "period" && this.end_at.length === 0;

            return !this.invalidStartDate && !this.invalidEndDate && this.validateReminders()
        },
        validateReminders() {
            if (!this.event_reminder_email) {
                return true;
            }
            const start_at_time = moment(this.start_at + (this.start_at_time ? ' ' + this.start_at_time : ''));
            let has_errors = false;
            // console.log(this.reminders)
            this.reminders.forEach((reminder, index) => {
                if (!reminder.reminder_units) {
                    has_errors = true;
                    this.reminders[index].error = this.trans('forms').training.reminder_must_specify_when_to_send;
                } else if (!reminder.processed_at
                    && moment().isAfter(start_at_time.clone().subtract(reminder.reminder_units, reminder.reminder_unit_type))) {
                    has_errors = true;
                    this.reminders[index].error = this.trans('forms').training.reminder_in_past;
                } else {
                    this.reminders[index].error = null;
                }
            });

            return !has_errors;
        },
        setMaxDate() {
            this.start_max_date = this.end_at
        },
        setMinDate() {
            this.end_min_date = this.start_at
        },
        addReminder() {
            this.reminders.push(this.newReminder());
        },
        removeReminder(index) {
            if (this.reminders.length > 1) {
                this.reminders.splice(index, 1);
            }
        },
        newReminder: () => {
            return {
                reminder_units: 1,
                reminder_unit_type: 'day',
                processed_at: null,
            }
        },
        checkNumber($event) {
            if (['.', ','].includes($event.key)) {
                $event.preventDefault();
            }
        }
    },
    watch: {
        event_reminder_email: function (newValue) {
            if (!newValue) {
                this.start_at_time = null;
                this.additional_info = null;
                this.reminders = [];
            } else if (!this.reminders.length) {
                this.reminders.push(this.newReminder());
            }
        }
    }
}
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
    transition: opacity .5s;
}

.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */
{
    opacity: 0;
}
</style>
